import type { Map } from 'immutable';
import type { Moment } from 'moment';

import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { duckAppName } from '@/constants';
import ApplicationRefEntity, {
  type ApplicationRefEntityRecord,
} from '@/entities/applications/ApplicationRef';
import UserEntity, { type UserEntityRecord } from '@/entities/User';

import type { MockRequestOutcome } from './enums';
import MockEndpointRefEntity, {
  type MockEndpointRefEntityRecord,
} from './MockEndpointRef';
import MockResponseRefEntity, {
  type MockResponseRefEntityRecord,
} from './MockResponseRef';
import MockSystemRefEntity, {
  type MockSystemRefEntityRecord,
} from './MockSystemRef';

class MockRequestLogEntity extends Entity {
  static paginated = true;

  static paths = {
    apiBase: '/mock_api/v1/mock_request_log/',
  };

  static fields: EntityFields<MockRequestLogEntityFields> = {
    uuid: new Fields.IdField(),
    created: new Fields.DateTimeField({ blank: true }),
    system: new Fields.EntityField({
      entity: MockSystemRefEntity,
      blank: true,
    }),
    system_slug: new Fields.CharField({ blank: true }),
    application: new Fields.EntityField({
      entity: ApplicationRefEntity,
      blank: true,
    }),
    user: new Fields.EntityField({ entity: UserEntity, blank: true }),
    endpoint: new Fields.EntityField({
      entity: MockEndpointRefEntity,
      blank: true,
    }),
    response: new Fields.EntityField({
      entity: MockResponseRefEntity,
      blank: true,
    }),
    method: new Fields.CharField({ blank: true }),
    path: new Fields.CharField({ blank: true }),
    query_string: new Fields.CharField({ blank: true }),
    request_headers: new Fields.AnyField({ blank: true }),
    request_body: new Fields.TextField({ blank: true }),
    request_body_truncated: new Fields.BooleanField({ blank: true }),
    remote_addr: new Fields.CharField({ blank: true }),
    path_params: new Fields.AnyField({ blank: true }),
    status_code: new Fields.IntegerField({ blank: true }),
    response_body: new Fields.TextField({ blank: true }),
    outcome: new Fields.CharField({ blank: true }),
    duration_ms: new Fields.IntegerField({ blank: true }),
  };

  static dataToRecord(data: Partial<MockRequestLogEntityData>) {
    return super.dataToRecord(data) as MockRequestLogEntityRecord;
  }
}

export type MockRequestLogEntityFields = {
  uuid: string;
  created: Moment | null;
  system: MockSystemRefEntityRecord | null;
  system_slug: string;
  application: ApplicationRefEntityRecord | null;
  user: UserEntityRecord | null;
  endpoint: MockEndpointRefEntityRecord | null;
  response: MockResponseRefEntityRecord | null;
  method: string;
  path: string;
  query_string: string;
  request_headers: Map<string, string> | null;
  request_body: string;
  request_body_truncated: boolean;
  remote_addr: string | null;
  path_params: Map<string, string> | null;
  status_code: number | null;
  response_body: string;
  outcome: MockRequestOutcome;
  duration_ms: number | null;
};

export type MockRequestLogEntityData = Omit<
  MockRequestLogEntityFields,
  | 'created'
  | 'system'
  | 'application'
  | 'user'
  | 'endpoint'
  | 'response'
  | 'request_headers'
  | 'path_params'
> & {
  created: string;
  system: { uuid: string; name: string; slug: string } | null;
  application?: {
    uuid: string;
    name: string | null;
    slug: string | null;
  } | null;
  user?: Record<string, unknown> | null;
  endpoint: {
    uuid: string;
    method: string;
    path_pattern: string;
    name: string;
  } | null;
  response: { uuid: string; name: string; status_code: number } | null;
  request_headers: Record<string, string>;
  path_params: Record<string, string>;
};

export type MockRequestLogEntityRecord =
  EntityRecord<MockRequestLogEntityFields>;

MockRequestLogEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: MockRequestLogEntity,
  name: 'MockRequestLogEntity',
});

export default MockRequestLogEntity;
